import { useEffect, useId, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CATALOG_TABS, type CategoryId, type ProductPlaceholder } from "@/data/catalog";
import { useAvailability } from "@/lib/availability";
import { useCatalog } from "@/lib/catalog-context";
import { config } from "@/lib/config";
import "./Landing.css";

const VALID_TABS: CategoryId[] = ["flower", "preRolls", "edibles", "specials"];

const PROMO_TEXT = `Text NEW to ${config.contactPhoneDisplay} to receive your customer #... Use it for $20 off your 1st order. Share it for $20 off a future order for each new referred customer. All orders must be a $100 minimum before applied discounts.`;

function parseTab(value: string | null): CategoryId {
  if (value && VALID_TABS.includes(value as CategoryId)) return value as CategoryId;
  return "flower";
}

function ProductCard({ product }: { product: ProductPlaceholder }) {
  const { productHasShopStock } = useAvailability();
  const soldOut = !productHasShopStock(product.slug, product.flavors);
  return (
    <li className={`product-card${soldOut ? " product-card--sold-out" : ""}`}>
      <Link to={`/product/${product.slug}`} className="product-card__link">
        <div className="product-card__media">
          {soldOut && <span className="product-card__sold-badge">Out of Stock</span>}
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt=""
              className="product-card__photo"
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="product-card__media-placeholder">
              <span className="product-card__media-icon" aria-hidden="true">
                &#x1F33F;
              </span>
            </div>
          )}
        </div>
        <div className="product-card__body">
          <h3 className="product-card__name">{product.name}</h3>
          <ul className="product-card__tiers" aria-label="Pricing">
            {product.tiers.map((row) => (
              <li key={row.qty} className="product-card__tier">
                <span className="product-card__tier-qty">{row.qty}</span>
                <span className="product-card__tier-price">{row.price}</span>
              </li>
            ))}
          </ul>
        </div>
      </Link>
    </li>
  );
}

export function Landing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<CategoryId>(() => parseTab(searchParams.get("tab")));
  const tabListId = useId();

  useEffect(() => {
    setTab(parseTab(searchParams.get("tab")));
  }, [searchParams]);

  function selectTab(id: CategoryId) {
    setTab(id);
    setSearchParams({ tab: id }, { replace: true });
  }

  useEffect(() => {
    const root = document.documentElement;
    let frame = 0;
    let t = 0;

    function tick() {
      t += 0.003;
      const hue = 142 + Math.sin(t) * 6;
      root.style.setProperty("--hue-shift", String(Math.round(hue)));
      frame = requestAnimationFrame(tick);
    }

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  const { flowerSections, placeholders } = useCatalog();
  const flatProducts = tab === "flower" ? null : placeholders[tab];

  return (
    <div className="landing">
      <div className="landing__bg" aria-hidden="true" />

      <div className="landing__container" id="catalog">
        <nav className="landing__tabs" aria-labelledby={tabListId}>
          <p id={tabListId} className="sr-only">
            Product categories
          </p>
          <div className="landing__tab-row" role="tablist">
            {CATALOG_TABS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                id={`tab-${id}`}
                aria-selected={tab === id}
                aria-controls={`panel-${id}`}
                className={`landing__tab ${tab === id ? "landing__tab--active" : ""}`}
                onClick={() => selectTab(id)}
              >
                {label}
              </button>
            ))}
          </div>
        </nav>

        <div className="marquee" aria-label="Promotion">
          <div className="marquee__track">
            <span className="marquee__text">{PROMO_TEXT}</span>
            <span className="marquee__text" aria-hidden="true">
              {PROMO_TEXT}
            </span>
          </div>
        </div>

        <section
          id={`panel-${tab}`}
          role="tabpanel"
          aria-labelledby={`tab-${tab}`}
          className="landing__panel"
        >
          {tab === "flower" ? (
            <div className="landing__flower-sections">
              {flowerSections.map((section) => (
                <div key={section.title} className="landing__section">
                  <h2 className="landing__section-title">{section.title}</h2>
                  <ul className="product-grid">
                    {section.strains.map((p) => (
                      <ProductCard key={`${section.title}-${p.slug}`} product={p} />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className="product-grid">
              {flatProducts!.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
